import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { useTheme, nextTheme, THEME_LABELS } from '../../../context/ThemeContext';
import ThemeModeIcon from '../../../components/ThemeModeIcon/ThemeModeIcon';
import { MAIN_ORIGIN } from '../../../config/appBase';
import { isIvoriApexHost } from '../../../config/ivoriHost';
import './EduHeader.css';

const EXAMS = [
  ['LNAT', 'National Admissions Test for Law', '/lnat'],
  ['TMUA', 'Test of Mathematics for University Admission', '/tmua'],
  ['TARA', 'Test of Academic Reasoning for Admissions', '/tara'],
  ['UCAT', 'University Clinical Aptitude Test', '/ucat'],
  ['ESAT', 'Engineering and Science Admissions Test', '/esat'],
  ['Oxbridge', 'Tests and subject interviews', '/oxbridge'],
  ['Medicine', 'UCAT, MMI and panel interviews', '/medicine'],
  ['Dentistry', 'UCAT, MMI and panel interviews', '/dentistry'],
  ['Degree apprenticeships', 'Assessment centres and employer interviews', '/degree-apprenticeships'],
];

const USES = [
  ['For learners', '/for-learners'],
  ['For parents and carers', '/for-parents'],
  ['For teachers', '/for-teachers'],
  ['For schools', '/for-schools'],
];

const RESEARCH = [
  ['Methodology', '/method'],
  ['Insights', '/insights'],
];

// The company home is ivoriai.com/about, outside the /edu router basename, so
// About is a plain <a> (a separate page load), never <Link>. On other hosts
// (pathbuddy.com, localhost) /about is not a page, so it goes to ivoriai.com.
const COMPANY_HOME_PATH = '/about';

function companyHomeHref() {
  const hostname = typeof window === 'undefined' || !window.location ? '' : window.location.hostname;
  return isIvoriApexHost(hostname) ? COMPANY_HOME_PATH : `${MAIN_ORIGIN}${COMPANY_HOME_PATH}`;
}

function motionReduced() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function Mark({ on }) {
  if (!on) return null;
  return <span className="mkt-mark" aria-hidden="true" />;
}

function RowArrow() {
  return (
    <svg className="mkt-arrow" width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <path d="M3 9h11M10 4.5L14.5 9 10 13.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function EduHeader() {
  const { isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const upcoming = nextTheme(theme);
  const location = useLocation();
  const [open, setOpen] = useState(null);
  const [reduced] = useState(motionReduced);

  const path = location.pathname;
  const examsOn = open === 'exams' || (open === null && EXAMS.some((row) => path === row[2]));
  const usesOn = open === 'uses' || (open === null && USES.some((row) => path === row[1]));
  const learnOn = open === 'learn' || (open === null && (path === '/method' || path.indexOf('/method/') === 0 || path === '/insights' || path.indexOf('/insights/') === 0));
  const featuresOn = open === null && (path === '/features' || path.indexOf('/features/') === 0);
  const pricingOn = open === null && (path === '/pricing' || path === '/upgrade' || path === '/plans');

  const toggle = (key) => setOpen((current) => (current === key ? null : key));
  const modeLabel = `Switch to ${THEME_LABELS[upcoming].toLowerCase()} mode`;

  return (
    <header className="mkt-header">
      <div className="mkt-header-bar">
        <Link className="mkt-wordmark" to="/" aria-label="IVORI Edu home">
          <span className="mkt-wordmark-name">IVORI</span>
          <span className="mkt-wordmark-edu">
            <span>E</span>
            <span>d</span>
            <span>u</span>
            {reduced ? null : <i className="mkt-caret" data-animating="true" aria-hidden="true" />}
          </span>
        </Link>
        <nav className="mkt-nav" aria-label="Primary">
          <button
            type="button"
            aria-expanded={open === 'exams'}
            aria-controls="menu-exams"
            className={examsOn ? 'is-active' : undefined}
            onClick={() => toggle('exams')}
          >
            Prepare
            <svg className={open === 'exams' ? 'mkt-chev is-open' : 'mkt-chev'} width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <path d="M1.5 3.5l3.5 3.5 3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <Mark on={examsOn} />
          </button>
          <button
            type="button"
            aria-expanded={open === 'uses'}
            aria-controls="menu-uses"
            className={usesOn ? 'is-active' : undefined}
            onClick={() => toggle('uses')}
          >
            Built for
            <svg className={open === 'uses' ? 'mkt-chev is-open' : 'mkt-chev'} width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <path d="M1.5 3.5l3.5 3.5 3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <Mark on={usesOn} />
          </button>
          <Link to="/features" className={featuresOn ? 'is-active' : undefined}>
            Features
            <Mark on={featuresOn} />
          </Link>
          <button
            type="button"
            aria-expanded={open === 'learn'}
            aria-controls="menu-learn"
            className={learnOn ? 'is-active' : undefined}
            onClick={() => toggle('learn')}
          >
            Research
            <svg className={open === 'learn' ? 'mkt-chev is-open' : 'mkt-chev'} width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
              <path d="M1.5 3.5l3.5 3.5 3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <Mark on={learnOn} />
          </button>
          <Link to="/pricing" className={pricingOn ? 'is-active' : undefined}>
            Pricing
            <Mark on={pricingOn} />
          </Link>
          <a href={companyHomeHref()}>About</a>
        </nav>
        <div className="mkt-header-actions">
          <button type="button" className="mkt-theme" onClick={toggleTheme} aria-label={modeLabel} title={modeLabel}>
            <ThemeModeIcon mode={upcoming} />
          </button>
          {isAuthenticated ? (
            <Link className="mkt-signin" to="/chat">Open app</Link>
          ) : (
            <>
              <Link className="mkt-signin" to="/login">Sign In</Link>
              <Link className="mkt-button" to="/login?signup=true">Try IVORI</Link>
            </>
          )}
        </div>
      </div>
      {open === 'exams' ? (
        <div id="menu-exams" className="mkt-menu">
          <div className="mkt-menu-grid">
            {EXAMS.map(([name, full, href]) => (
              <Link key={href} to={href} onClick={() => setOpen(null)}>
                <span>
                  <span className="mkt-menu-name">{name}</span>
                  <span className="mkt-menu-full">{full}</span>
                </span>
                <RowArrow />
              </Link>
            ))}
          </div>
        </div>
      ) : null}
      {open === 'uses' ? (
        <div id="menu-uses" className="mkt-menu">
          <div className="mkt-menu-grid">
            {USES.map(([name, href]) => (
              <Link key={href} to={href} onClick={() => setOpen(null)}>
                <span className="mkt-menu-name">{name}</span>
                <RowArrow />
              </Link>
            ))}
          </div>
        </div>
      ) : null}
      {open === 'learn' ? (
        <div id="menu-learn" className="mkt-menu">
          <div className="mkt-menu-grid">
            {RESEARCH.map(([name, href]) => (
              <Link key={href} to={href} onClick={() => setOpen(null)}>
                <span className="mkt-menu-name">{name}</span>
                <RowArrow />
              </Link>
            ))}
          </div>
        </div>
      ) : null}
    </header>
  );
}
